import { FormikProvider, useFormik } from "formik";
import Title from "antd/es/typography/Title";
import { WidgetsLayout, WidgetsModal } from "pages/Dashboards/ui";
import React, { useEffect, useState } from "react";
import { useDashboardMainPageRequests, useModal } from "shared/hooks";
import { Page } from "shared/ui";
import { Button, Select, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { noop } from "shared/helpers";
import { useTranslation } from "react-i18next";
import {
  useGetLoggedUserQuery,
  usePatchUserMutation,
} from "shared/api/users/usersApi";
import { useGetAllDashboardsQuery } from "shared/api/dashboards/dashboardsApi";

const WelcomePage = () => {
  const { t } = useTranslation();

  const { data: currentUser } = useGetLoggedUserQuery();
  const { data: allDashboards } = useGetAllDashboardsQuery();
  const [patchUser] = usePatchUserMutation();

  const [selectedDashboardId, setSelectedDashboardId] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (currentUser?.default_dashboard) {
      setSelectedDashboardId(String(currentUser.default_dashboard));
    } else if (allDashboards && allDashboards.length > 0) {
      // Find "Welcome Template" dashboard by name
      const welcomeTemplate = allDashboards.find((dashboard) =>
        dashboard.name.toLowerCase().includes("welcome")
      );

      // If Welcome Template found, use it, otherwise fallback to ID 21 (hardcoded)
      const defaultId = welcomeTemplate ? welcomeTemplate.id : 21;
      setSelectedDashboardId(String(defaultId));
    }
  }, [currentUser, allDashboards]);

  const { dashboardsData, sortedWidgetsWithArticle } =
    useDashboardMainPageRequests({
      dashboardId: selectedDashboardId,
    });

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  const [isOpenWidgetsModal, showWidgetsModal, hideWidgetsModal] = useModal();

  const widgetsFormik = useFormik({
    initialValues: {
      id: dashboardsData?.id,
      name: dashboardsData?.name,
      widgets: dashboardsData?.widgets ?? [],
      settings: null,
      configuration: dashboardsData?.configuration ?? [],
    },
    enableReinitialize: true,
    onSubmit: noop,
  });

  const username = localStorage.getItem("username");

  const handleDashboardChange = async (value: string) => {
    setSelectedDashboardId(value);
    if (currentUser?.id) {
      try {
        await patchUser({
          id: currentUser.id,
          default_dashboard: Number(value),
        }).unwrap();
        message.success(
          t("welcome-page.dashboard-updated") ||
            "Default dashboard updated successfully"
        );
      } catch (error) {
        message.error(
          t("welcome-page.dashboard-update-error") ||
            "Failed to update default dashboard"
        );
      }
    }
  };

  return (
    <Page title={`${t("welcome-page.title")} ${username}`} isCenteredTitle>
      <FormikProvider value={widgetsFormik}>
        <div className="w-full h-full flex">
          <WidgetsModal
            isOpen={isOpenWidgetsModal}
            onClose={hideWidgetsModal}
          />

          <div className="w-full">
            <div className="p-3.75">
              <div className="flex justify-between items-center">
                <div className="flex gap-3 items-center">
                  <Button
                    style={{ backgroundColor: themeColor }}
                    type="primary"
                    onClick={showWidgetsModal}
                  >
                    {t("dashboards.add-widget")}
                    <PlusOutlined />
                  </Button>
                  <Select
                    style={{ minWidth: 200 }}
                    placeholder={
                      t("welcome-page.select-dashboard") || "Select Dashboard"
                    }
                    value={selectedDashboardId}
                    onChange={handleDashboardChange}
                    options={allDashboards?.map((dashboard) => ({
                      label: dashboard.name,
                      value: String(dashboard.id),
                    }))}
                  />
                </div>
              </div>
            </div>
            <div className="max-h-[75vh] overflow-x-auto overflow-y-scroll">
              <div className="w-full">
                <WidgetsLayout
                  dashboardsData={dashboardsData}
                  sortedWidgetsWithArticle={sortedWidgetsWithArticle}
                />
              </div>
            </div>
            <Title className="mt-4 !text-3xl" level={5}>
              <a
                className="!text-black"
                href="https://www.chainlinx.se/"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.chainlinx.se
              </a>
            </Title>
          </div>
        </div>
      </FormikProvider>
    </Page>
  );
};

export { WelcomePage };
